import { createSlice } from "@reduxjs/toolkit";

import {
  getAveragesActiveLoans,
  getAveragesLoans,
  getRepaymentSchedule,
  getTotalLoanBookValue,
  getSectorWise,
  getTotalNumberOfLoans,
  getTotalStockLoan,
  getStockWise,
  getTotalPortfolioValue,
  getBadLoansSummary,
} from "./api";

// Utility function to handle state updates
const handlePending = (state, key) => {
  state[key].loading = true;
};

const handleFulfilled = (state, key, action, prop = "data") => {
  state[key].loading = false;
  state[key][prop] = action.payload;
};

const handleRejected = (state, key, action) => {
  state[key].loading = false;
  state[key].error = action.error.message;
};

const dashBoardSlice = createSlice({
  name: "dashboard",

  initialState: {
    totalStockLoan: { data: 0, loading: false, error: {} },
    totalLoanBookValue: { data: 0, loading: false, error: {} },
    totalNumberOfLoans: { data: 0, loading: false, error: {} },
    averagesLoans: { data: 0, loading: false, error: {} },
    averagesActiveLoans: { data: 0, loading: false, error: {} },
    repaymentSchedule: {
      loading: false,
      error: null,
      filter: 'week',
      periodData: [],
      totals: {
        totalLoanAmount: 0,
        totalInterestFee: 0,
        totalValue: 0,
        totalInstallments: 0
      }
    },
    totalPortfolioValue: { data: {}, loading: false, error: {} },
    sectorData: {
      data: [],
      totalRows: 0,
      loading: false,
    },
    stockData: { data: [], totalRows: 0, loading: false },
    badLoansSummary: {
      data: {
        count: 0,
        amount: 0,
        percentage: 0,
        totalActiveLoans: 0,
        totalActiveLoanAmount: 0,
        totalLoans: 0,
        totalLoanAmount: 0,
      },
      loading: false,
      error: null,
    },
    backUrl: localStorage.getItem("backUrl"),
  },

  reducers: {
    setBackUrl(state, action) {
      localStorage.setItem("backUrl", action.payload);
    },
    setRepaymentFilter(state, action) {
      state.repaymentSchedule.filter = action.payload;
    }
  },

  extraReducers: (builder) => {
    // getTotalStockLoan
    builder
      .addCase(getTotalStockLoan.pending, (state) =>
        handlePending(state, "totalStockLoan")
      )
      .addCase(getTotalStockLoan.fulfilled, (state, action) => {
        state.sectorData.data = action.payload.data;
        state.sectorData.loading = false;
        state.sectorData.totalRows = action.payload.totalRecord;
      })
      .addCase(getTotalStockLoan.rejected, (state, action) =>
        handleRejected(state, "totalStockLoan", action)
      );

    // getTotalLoanBookValue
    builder
      .addCase(getTotalLoanBookValue.pending, (state) =>
        handlePending(state, "totalLoanBookValue")
      )
      .addCase(getTotalLoanBookValue.fulfilled, (state, action) =>
        handleFulfilled(state, "totalLoanBookValue", action)
      )
      .addCase(getTotalLoanBookValue.rejected, (state, action) =>
        handleRejected(state, "totalLoanBookValue", action)
      );

    // getSectorWise
    builder
      .addCase(getSectorWise.pending, (state) =>
        handlePending(state, "sectorData")
      )
      .addCase(getSectorWise.fulfilled, (state, action) => {
        state.sectorData.data = action.payload.data;
        state.sectorData.loading = false;
        state.sectorData.totalRows = action.payload.totalRecord;
      })
      .addCase(getSectorWise.rejected, (state, action) =>
        handleRejected(state, "sectorData", action)
      );

    // getTotalNumberOfLoans
    builder
      .addCase(getTotalNumberOfLoans.pending, (state) =>
        handlePending(state, "totalNumberOfLoans")
      )
      .addCase(getTotalNumberOfLoans.fulfilled, (state, action) =>
        handleFulfilled(state, "totalNumberOfLoans", action)
      )
      .addCase(getTotalNumberOfLoans.rejected, (state, action) =>
        handleRejected(state, "totalNumberOfLoans", action)
      );

    builder
      .addCase(getStockWise.pending, (state) =>
        handlePending(state, "stockData")
      )
      .addCase(getStockWise.fulfilled, (state, action) => {
        state.stockData.data = action.payload.data;
        state.stockData.loading = false;
        state.stockData.totalRows = action.payload.totalRecord;
      })
      .addCase(getStockWise.rejected, (state, action) =>
        handleRejected(state, "stockData", action)
      );

    // getAveragesLoans
    builder
      .addCase(getAveragesLoans.pending, (state) =>
        handlePending(state, "averagesLoans")
      )
      .addCase(getAveragesLoans.fulfilled, (state, action) =>
        handleFulfilled(state, "averagesLoans", action)
      )
      .addCase(getAveragesLoans.rejected, (state, action) =>
        handleRejected(state, "averagesLoans", action)
      );

    // getAveragesActiveLoans
    builder
      .addCase(getAveragesActiveLoans.pending, (state) =>
        handlePending(state, "averagesActiveLoans")
      )
      .addCase(getAveragesActiveLoans.fulfilled, (state, action) =>
        handleFulfilled(state, "averagesActiveLoans", action)
      )
      .addCase(getAveragesActiveLoans.rejected, (state, action) =>
        handleRejected(state, "averagesActiveLoans", action)
      );

    // getRepaymentSchedule
    builder
      .addCase(getRepaymentSchedule.pending, (state) => {
        state.repaymentSchedule.loading = true;
        state.repaymentSchedule.error = null;
      })
      .addCase(getRepaymentSchedule.fulfilled, (state, action) => {
        state.repaymentSchedule.loading = false;
        state.repaymentSchedule.error = null;
        state.repaymentSchedule.filter = action.payload.filter;
        state.repaymentSchedule.periodData = action.payload.periodData;
        state.repaymentSchedule.totals = action.payload.totals;
      })
      .addCase(getRepaymentSchedule.rejected, (state, action) => {
        state.repaymentSchedule.loading = false;
        state.repaymentSchedule.error = action.error.message;
      });

    // totalPortfolioValue
    builder
      .addCase(getTotalPortfolioValue.pending, (state) =>
        handlePending(state, "totalPortfolioValue")
      )
      .addCase(getTotalPortfolioValue.fulfilled, (state, action) =>
        handleFulfilled(state, "totalPortfolioValue", action)
      )
      .addCase(getTotalPortfolioValue.rejected, (state, action) =>
        handleRejected(state, "totalPortfolioValue", action)
      );
    builder
      .addCase(getBadLoansSummary.pending, (state) => {
        state.badLoansSummary.loading = true;
        state.badLoansSummary.error = null;
      })
      .addCase(getBadLoansSummary.fulfilled, (state, action) => {
        state.badLoansSummary.loading = false;
        state.badLoansSummary.data = action.payload.data;
        state.badLoansSummary.error = null;
      })
      .addCase(getBadLoansSummary.rejected, (state, action) => {
        state.badLoansSummary.loading = false;
        state.badLoansSummary.error = action.error.message;
      });
  },
});

export const { setBackUrl, setRepaymentFilter } = dashBoardSlice.actions;

export default dashBoardSlice.reducer;